package com.advaita.cloud.drive.ui.store

import com.advaita.cloud.drive.shared.CuratedInputDto
import com.advaita.cloud.drive.shared.CuratedInputFormatWire
import com.advaita.cloud.drive.shared.CuratedKnobDto
import com.advaita.cloud.drive.shared.CuratedWorkflowDto

/**
 * ADV-669 — the catalog of **opinionated use-cases (OPCs)**, converted from what wisp's
 * registry serves into the picker's own model.
 *
 * ## There is no catalog in this file any more, and that is the point
 *
 * This used to hold a hardcoded `OPC_CATALOG`: title, description, `jobType`, and an
 * input list, all hand-copied from documents a curator had registered with wisp. It
 * drifted, and it drifted invisibly. On 2026-08-17 our copy said `demo-read-qc` while
 * the registered document's `id` — which `TemplateExpand` turns straight into the
 * matcher key (`when.job_type = s["id"]`) — was `demo`. The qa run that followed was
 * *accepted*: wisp's `submit` mints a run_id without matching a template,
 * `validate_params` finds none and passes on its cut-over conditional, and
 * `enqueue_request` answers 202 with the run_id. Drive logged a healthy submit and the
 * scientist's samplesheet sat at `Processing` forever, with no error on either side.
 *
 * The registry is the only thing that knows what will route, so the registry is what we
 * render. `GET /api/pipeline/curated/catalog` → wisp `GET /offerings`, which is wisp's
 * projection over the SAME assembled templates its matcher uses — in its own words, so
 * that "the catalog can never disagree with what a submit will actually match".
 *
 * What is left here is only the conversion, kept as pure functions so it is testable
 * under jsTest without fetch.
 */

/** One offering → the picker's spec. */
fun CuratedWorkflowDto.toSpec(): CuratedWorkflowSpec = CuratedWorkflowSpec(
    title = title,
    description = description,
    // Verbatim. This is the curator's registered id and the key wisp matches on; it is
    // not ours to normalise, prettify or suffix.
    jobType = jobType,
    inputs = inputs.map { it.toSpec() },
    knobs = knobs.map { it.toSpec() },
)

/** One offering's scalar knob → the picker's knob spec. */
fun CuratedKnobDto.toSpec(): CuratedKnobSpec = CuratedKnobSpec(
    name = name,
    // Kept SEPARATE, deliberately — not joined into one string as an input's are. Joining
    // with a space assumes the curator ended `description` with punctuation, and when they
    // didn't the two ran together mid-sentence ("…a named reference set in wisp One choice
    // sets the genome…"). Two fields the curator wrote as two thoughts render as two
    // elements, and no punctuation of theirs gets guessed at.
    description = description,
    helpText = helpText,
    required = required,
    // The declared default becomes the control's STARTING value, not a value sent behind
    // the scientist's back. Nothing else in the chain applies it (wisp's `hydrate` takes
    // the submitted knobs verbatim), so this is where a curator's default becomes real.
    initial = default.orEmpty(),
    choices = choices,
    isReference = format == KNOB_FORMAT_REFERENCE,
)

/** `format: reference` — the value names a wisp reference SET, not a scalar. */
const val KNOB_FORMAT_REFERENCE = "reference"

/** One offering's file input → the picker's input spec. */
fun CuratedInputDto.toSpec(): CuratedInputSpec = CuratedInputSpec(
    name = name,
    format = when (format) {
        CuratedInputFormatWire.FILE_SET -> CuratedInputFormat.FILE_SET
        // file-path, and anything unrecognised. The backend already dropped inputs whose
        // format is neither, so this branch is the safe default rather than a decision.
        else -> CuratedInputFormat.FILE_PATH
    },
    targetPath = targetPath,
    // The curator's `description` is the label; `help_text` is their longer prose. Join
    // them rather than pick one — a curator who wrote both meant both to be read, and
    // the picker has a single description slot.
    description = listOfNotNull(description.ifBlank { null }, helpText).joinToString(" "),
    required = required,
    // The schema's `pattern` when the curator wrote one, otherwise UNCONSTRAINED.
    //
    // The old hardcoded catalog fell back to an invented `acceptedExtensions` list here
    // (".fastq.gz", ".fq.gz") whenever `pattern` was absent. That fallback is gone
    // deliberately: it was never a contract term — session E made it up — and inventing
    // a constraint the curator did not declare means the picker can dim a file the
    // pipeline would happily have taken. Unconstrained is the honest reading of "the
    // curator declared no pattern".
    pattern = pattern?.let { runCatching { Regex(it) }.getOrNull() },
)

/** The whole catalog, converted. */
fun List<CuratedWorkflowDto>.toSpecs(): List<CuratedWorkflowSpec> = map { it.toSpec() }
